/**
 * 1stbuddha — faq.js
 *
 * <faq-page> (sections/faq.liquid)
 *   - the search field filters questions by question and answer text; groups
 *     without matches hide, matching answers open, "No matching questions"
 *     shows when nothing matches; Esc clears the field
 *   - topic links (desktop nav / mobile chips) get aria-current for the group
 *     being read
 */
(() => {
  if (customElements.get('faq-page')) return;

  const normalise = (text) =>
    text
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '');

  class FaqPage extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-faq-search]');
      this.items = [...this.querySelectorAll('[data-faq-item]')].map((item) => ({
        item,
        text: normalise(item.textContent),
      }));
      this.groups = [...this.querySelectorAll('[data-faq-group]')];
      this.empty = this.querySelector('[data-faq-empty]');

      this.input?.addEventListener('input', () => this.filter());
      this.input?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.input.value) {
          event.preventDefault();
          this.input.value = '';
          this.filter();
        }
      });
      this.spy();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    filter() {
      const words = normalise(this.input.value.trim()).split(/\s+/).filter(Boolean);
      let matches = 0;
      this.items.forEach(({ item, text }) => {
        const hit = words.every((word) => text.includes(word));
        item.hidden = !hit;
        if (hit) matches += 1;
        if (words.length && hit && item.dataset.open !== 'true') item.querySelector('.accordion__trigger')?.click();
      });
      this.groups.forEach((group) => {
        group.hidden = !group.querySelector('[data-faq-item]:not([hidden])');
      });
      if (this.empty) this.empty.hidden = matches > 0;
    }

    spy() {
      const links = [...this.querySelectorAll('[data-faq-topic]')];
      if (!links.length || !('IntersectionObserver' in window)) return;
      const setCurrent = (id) =>
        links.forEach((link) => {
          if (link.getAttribute('href') === `#${id}`) link.setAttribute('aria-current', 'true');
          else link.removeAttribute('aria-current');
        });
      this.observer = new IntersectionObserver(
        (entries) => {
          const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          if (visible[0]) setCurrent(visible[0].target.id);
        },
        { rootMargin: '-20% 0px -60% 0px' }
      );
      this.groups.filter((g) => g.id).forEach((group) => this.observer.observe(group));
      if (this.groups[0]?.id) setCurrent(this.groups[0].id);
    }
  }

  customElements.define('faq-page', FaqPage);
})();
